import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { HelperService } from "@services/helper.service";
import { OperationService } from "@services/operation.service";
import { ExonerationService } from "@services/exoneration.service";
import { NoticeService } from "@services/notice.service";
import { ModalService } from "@services/modal.service";
import { IndividualOperationRequest } from "@models/individualOperationRequest.model";
import { PaginationComponent } from "@components/layout/pagination/pagination.component";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { DueDateTitleValueRequest, OperationItem } from "@models/operationItem.model";
import { ExonerateGuaranteRequest } from "@models/exonerateGuaranteRequest.model";
import { CONSTANTS } from "@config/constants";
import { PubSubService } from "@services/pubsub.service";
import { AuthService } from "@services/auth.service";
import { UpdateTitleValueDigitalRequest } from "@app/models/updateTitleValueDigitalRequest";
import { Subscription, of, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import * as moment from "moment";

type SortField =
  | ''
  | 'guaranteedValue'
  | 'expiration'
  | 'daysMarkNotice'
  | 'exonerateDays'
  | 'balance';

@Component({
  selector: "app-tab-individual-operation",
  templateUrl: "./tab-individual-operation.component.html",
  styleUrls: ["./tab-individual-operation.component.scss"],
})
export class TabIndividualOperationComponent implements OnInit, OnDestroy {
  @ViewChild("paginationComponent") paginationComponent: PaginationComponent;
  @ViewChild("LoginModal") loginModal: any;

  public individualOperationRequest: IndividualOperationRequest = {
    page: 1,
    search: "",
    endDate: moment().toDate(),
    startDate: moment().subtract(7, "days").toDate(),
    force: true,
    orderBy: null,
    orderDirection: null,
    estado: null
  };

  private reloadSub: Subscription;
  public totalPages: number = 1;
  public pageLimit: number = 10;
  public operationType: number = 1;
  public someElementChecked = false;
  public operations: Array<OperationItem> = [];
  public selectedOperations: Array<OperationItem> = [];
  public totalNoticeAvailable: boolean = false;
  public noticeAvailableDays: boolean = false;
  public allCheck: boolean = false;
  public isLoadingDueDate = false;
  public isLoadingDueDateLimits = false;
  public dueDateLimitsFailed = false;
  private dueDateRequestId = 0;
  private expirationSaveRequestId = 0;

  public minInput: Date = moment().subtract(2, "year").toDate();
  public maxInput: Date = moment().add(2, "years").toDate();

  // Orden
  sort = {
    field: '' as SortField,
    direction: 'asc' as 'asc' | 'desc',
  };

  // Opciones fijas de estado (label = lo que ve el usuario, value = lo que espera el back)
  estadoOptions = [
    { label: 'Todos los estados', value: null },
    { label: 'Exonerada parcial', value: 'Exonerada parcial' },
    { label: 'Al día', value: 'Al Dia' },
    { label: 'Aviso no pago', value: 'Aviso no pago' }
  ];

  selectedEstado: string | null = null;
  estadoMenuOpen = false;

  constructor(
    private auth: AuthService,
    public helper: HelperService,
    public operationService: OperationService,
    public exonerationService: ExonerationService,
    public noticeService: NoticeService,
    public modal: ModalService,
    private spinner: NgxSpinnerService,
    private pubSubService: PubSubService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.reloadSub = this.pubSubService.messageSent.subscribe(() => {
      this.paginationComponent.reset();
      this.individualOperationRequest = {
        page: 1,
        search: "",
        endDate: moment().add(1, "month").toDate(),
        startDate: moment().toDate(),
        force: true,
        orderBy: null,
        orderDirection: null,
        estado: null
      };
      this.sort = { field: '', direction: 'asc' };
      this.selectedEstado = null;
      this.loadOperations();
    });

    this.loadOperations();
  }

  ngOnDestroy() {
    if (this.reloadSub) {
      this.reloadSub.unsubscribe();
      this.reloadSub = null;
    }
  }

  /** Mapea el campo de la columna al nombre que espera el servicio en &ordenar= */
  private mapSortFieldToApi(field: SortField): string {
    switch (field) {
      case 'guaranteedValue':
        return 'VALOR_GARANTIZADO';
      case 'expiration':
        return 'VENCIMIENTO';
      case 'daysMarkNotice':
        return 'DIAS_MARCAR_AVISO';
      case 'exonerateDays':
        return 'DIAS_EXONERAR';
      case 'balance':
        return 'SALDO';
      default:
        return 'DOCUMENTO';
    }
  }

  private loadOperations() {
    this.selectedOperations = [];
    this.dueDateRequestId++;
    this.expirationSaveRequestId++;
    this.spinner.show();

    this.operationService
      .getOperationList(this.individualOperationRequest)
      .subscribe(
        (resp) => {
          this.operations = resp.operations;
          this.totalPages = resp.totalPages || 1;
          this.spinner.hide();
        },
        (err) => {
          if (err.status === 401) {
            this.modal.show("modalLogoutErrorComponent");
          }
          this.spinner.hide();
        }
      );
  }

  // Click en encabezado para ordenar columnas numéricas/fechas (ASC -> DESC -> SIN ORDEN)
  onSort(field: SortField): void {
    // Si hay un filtro de estado activo, lo quitamos al empezar a usar ordenamiento normal
    this.selectedEstado = null;

    // 1) Si cambio de columna: primer click -> ASC
    if (this.sort.field !== field) {
      this.sort.field = field;
      this.sort.direction = 'asc';

      this.individualOperationRequest.orderBy = this.mapSortFieldToApi(field);
      this.individualOperationRequest.orderDirection = 'ASC';
    } else {
      // Misma columna
      if (this.sort.direction === 'asc') {
        // Segundo click -> DESC
        this.sort.direction = 'desc';

        this.individualOperationRequest.orderBy = this.mapSortFieldToApi(field);
        this.individualOperationRequest.orderDirection = 'DESC';
      } else if (this.sort.direction === 'desc') {
        // Tercer click -> quitar orden
        this.sort.field = '';
        this.sort.direction = 'asc'; // valor neutro interno

        this.individualOperationRequest.orderBy = null;
        this.individualOperationRequest.orderDirection = null;
      }
    }

    this.individualOperationRequest.page = 1;
    this.paginationComponent.reset();

    this.loadOperations();
  }

  // Abrir/cerrar menú de estados
  toggleEstadoMenu(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.estadoMenuOpen = !this.estadoMenuOpen;
  }

  // Selección de estado (null = Todos)
  selectEstado(estado: string | null): void {
    this.selectedEstado = estado;

    // Al usar filtro por estado, el orden se maneja como:
    // &ordenar=ESTADO&ordenamiento=<estado>
    if (estado) {
      // Cuando hay un estado seleccionado, no hay columna "sortable" activa
      this.sort.field = '';
      this.sort.direction = 'asc';

      this.individualOperationRequest.orderBy = 'ESTADO';
      this.individualOperationRequest.orderDirection = estado; // Ej: 'Al Dia', 'Exonerado parcial'
    } else {
      // "Todos los estados": quitar filtro por estado
      // Si no hay sort activo, limpiamos también ordenar/ordenamiento
      if (!this.sort.field) {
        this.individualOperationRequest.orderBy = null;
        this.individualOperationRequest.orderDirection = null;
      }
      // Si sí hay sort.field activo, se respeta el orden que ya esté configurado
    }

    this.individualOperationRequest.page = 1;
    this.paginationComponent.reset();

    this.loadOperations();
    this.estadoMenuOpen = false;
  }

  search(dateRange: Array<Date>) {
    if (dateRange[0]) {
      this.individualOperationRequest.startDate = dateRange[0];
    }
    if (dateRange[1]) {
      this.individualOperationRequest.endDate = dateRange[1];
    }
    if (dateRange[0] && dateRange[1]) {
      this.individualOperationRequest.page = 1;
      this.paginationComponent.reset();
      this.loadOperations();
    }
  }

  pageSelected(page: any) {
    this.individualOperationRequest.page = page.currentPage;
    this.loadOperations();
  }

  toggleSelected() {
    const willCheckAll = !this.allCheck;
    this.operations = this.operations.map((operationItem: OperationItem) => {
      operationItem.check = willCheckAll && !this.isDigitalOrigin(operationItem);
      return operationItem;
    });
    this.selectedOperations = willCheckAll
      ? this.operations.filter((operationItem: OperationItem) => operationItem.check)
      : [];
    this.totalNoticeAvailable = true;
    this.noticeAvailableDays = true;
    this.selectedOperations.forEach((selectedOperation: OperationItem) => {
      if (this.hasNoticeNotPayment(selectedOperation)) {
        this.totalNoticeAvailable = false;
      } else {
        if (!selectedOperation.daysMarkNotice) {
          this.noticeAvailableDays = false;
        }
      }
    });

    this.allCheck = willCheckAll;
  }

  hasNoticeNotPayment(item: OperationItem): boolean {
    return !!(
      item &&
      item.status &&
      item.status.toLowerCase() === CONSTANTS.OPERATIONS.NOTICE_NOT_PAYMENT.toLowerCase()
    );
  }

  onSearch(documentNumber: string) {
    this.individualOperationRequest.search = documentNumber || "";
    this.individualOperationRequest.page = 1;
    this.paginationComponent.reset();
    this.loadOperations();
  }

  isDigitalSelectable(item: OperationItem): boolean {
    return !!(
      item &&
      item.check &&
      this.isDigitalOrigin(item) &&
      this.selectedOperations.length === 1 &&
      !this.hasNoticeNotPayment(item)
    );
  }

  isDigitalEditable(item: OperationItem): boolean {
    return (
      this.isDigitalSelectable(item) &&
      !this.isLoadingDueDateLimits &&
      !this.dueDateLimitsFailed
    );
  }

  isDigitalOrigin(item: OperationItem): boolean {
    return !!(
      item &&
      item.origin &&
      item.origin.toLowerCase().indexOf('digital') !== -1 &&
      (!item.productType || item.productType.toLowerCase().indexOf('trespg') === -1)
    );
  }

  isCheckboxDisabled(item: OperationItem): boolean {
    if (item.check) {
      return false;
    }
    if (this.selectedOperations.length === 0) {
      return false;
    }
    return (
      this.isDigitalOrigin(item) ||
      this.selectedOperations.some((selectedOperation: OperationItem) => this.isDigitalOrigin(selectedOperation))
    );
  }

  loadDueDateLimits(item: OperationItem): void {
    item.expirationDraft = item.expiration;

    const request: DueDateTitleValueRequest = {
      documentType: item.documentType,
      documentNumber: item.documentNumber,
      titleValueNumber: item.titleValue,
      authorization: item.authorization,
    };

    const requestId = ++this.dueDateRequestId;
    this.isLoadingDueDate = true;
    this.isLoadingDueDateLimits = true;
    this.dueDateLimitsFailed = false;

    this.operationService.getDueDateTitleValue(request).subscribe({
      next: (resp: any) => {
        if (requestId !== this.dueDateRequestId) {
          return;
        }
        this.minInput = moment(resp.FECHA_AUTORIZACION, 'YYYYMMDD').toDate();
        this.maxInput = moment(resp.FECHA_MAXIMA, 'YYYYMMDD').toDate();
        this.isLoadingDueDate = false;
        this.isLoadingDueDateLimits = false;
      },
      error: (err: any) => {
        if (requestId !== this.dueDateRequestId) {
          return;
        }
        console.error('Error fetching due date:', err);
        this.isLoadingDueDate = false;
        this.isLoadingDueDateLimits = false;
        this.dueDateLimitsFailed = true;
      },
    });
  }

  checkOperation(operationItem: OperationItem) {
    const selectedIndex = this.selectedOperations.findIndex(
      (selectedOperation: OperationItem) =>
        `${operationItem.authorization}-${operationItem.titleValue}` ===
        `${selectedOperation.authorization}-${selectedOperation.titleValue}`
    );

    if (selectedIndex < 0) {
      this.selectedOperations.push(operationItem);
    } else {
      this.selectedOperations.splice(selectedIndex, 1);
    }

    this.totalNoticeAvailable = true;
    this.noticeAvailableDays = true;
    this.selectedOperations.forEach((selectedOperation: OperationItem) => {
      if (this.hasNoticeNotPayment(selectedOperation)) {
        this.totalNoticeAvailable = false;
      } else {
        if (!selectedOperation.daysMarkNotice) {
          this.noticeAvailableDays = false;
        }
      }
    });

    if (this.isDigitalSelectable(operationItem)) {
      this.loadDueDateLimits(operationItem);
    }
  }

  withdrawal() {
    this.operationType = 4;
    this.modal.show("operationsConfirm", {
      type: "withdrawal",
      name: this.selectedOperations[0].name
        .toLowerCase()
        .replace(/\b\S/g, function (t) {
          return t.toUpperCase();
        }),
      titleValueType:
        this.selectedOperations[0].titleValueType === CONSTANTS.OPERATIONS.CHECK
          ? "cheque"
          : "factura",
      title: "Confirmar desistimiento",
      billNumber: this.selectedOperations[0].titleValue,
      billPrice: this.selectedOperations[0].valueToExonerate,
      dueDate: this.selectedOperations[0].expiration,
    });
  }

  totalExoneration() {
    if (this.isamount()) {
      (window as any).gtag("event", "seleccion-obligacion", {
        event_category: "exoneracion-parcial",
        event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
      });
      const amountOperation: OperationItem = this.getAmountItem();
      if (amountOperation) {
        this.modal.show("operationsConfirm", {
          type: "partialExoneration",
          titleValueType:
            amountOperation.titleValueType === CONSTANTS.OPERATIONS.CHECK
              ? "cheque"
              : "factura",
          title: "Confirmar exoneración parcial",
          name: amountOperation.name
            .toLowerCase()
            .replace(/\b\S/g, function (t) {
              return t.toUpperCase();
            }),
          billNumber: amountOperation.titleValue,
          billPrice: amountOperation.amount,
        });
        this.operationType = 2;
      }
    } else {
      (window as any).gtag("event", "seleccion-obligacion", {
        event_category: "exoneracion-total",
        event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
      });
      const billTotal = this.selectedOperations.reduce(
        (acc: number, it: OperationItem) =>
          (acc += parseInt(it.valueToExonerate)),
        0
      );

      if (this.selectedOperations.length > 1) {
        this.modal.show("operationsConfirm", {
          type: "totalExonerationMultiple",
          titleValueType:
            this.selectedOperations[0].titleValueType ===
            CONSTANTS.OPERATIONS.CHECK
              ? "cheque"
              : "factura",
          title: "Confirmar exoneración total",
          billNumber: this.selectedOperations.length,
          billTotal,
        });
      } else {
        this.modal.show("operationsConfirm", {
          type: "totalExoneration",
          titleValueType:
            this.selectedOperations[0].titleValueType ===
            CONSTANTS.OPERATIONS.CHECK
              ? "cheque"
              : "factura",
          title: "Confirmar exoneración total",
          name: this.selectedOperations[0].name
            .toLowerCase()
            .replace(/\b\S/g, function (t) {
              return t.toUpperCase();
            }),
          billNumber: this.selectedOperations[0].titleValue,
          billPrice: this.selectedOperations[0].valueToExonerate,
          dueDate: this.selectedOperations[0].expiration,
        });
      }

      this.operationType = 1;
    }
  }

  totalNotice() {
    const billTotal = this.selectedOperations.reduce(
      (acc: number, it: OperationItem) =>
        (acc += parseInt(it.valueToExonerate)),
      0
    );

    if (this.selectedOperations.length > 1) {
      this.modal.show("operationsConfirm", {
        type: "totalExonerationMultiple",
        title: "Confirmar aviso de no pago",
        origin: this.selectedOperations[0].origin,
        titleValueType:
          this.selectedOperations[0].titleValueType ===
          CONSTANTS.OPERATIONS.CHECK
            ? "cheque"
            : "factura",
        billNumber: this.selectedOperations.length,
        billTotal,
      });
    } else {
      (window as any).gtag("event", "seleccion-obligacion", {
        event_category: "aviso-de-no-pago",
        event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
      });
      this.modal.show("operationsConfirm", {
        type: "nonPayment",
        titleValueType:
          this.selectedOperations[0].titleValueType ===
          CONSTANTS.OPERATIONS.CHECK
            ? "cheque"
            : "factura",
        title: "Confirmar aviso de no pago",
        name: this.selectedOperations[0].name
          .toLowerCase()
          .replace(/\b\S/g, function (t) {
            return t.toUpperCase();
          }),
        billNumber: this.selectedOperations[0].titleValue,
        billPrice: this.selectedOperations[0].valueToExonerate,
        dueDate: this.selectedOperations[0].expiration,
        origin: this.selectedOperations[0].origin,
        productType: this.selectedOperations[0].productType
      });
    }

    this.operationType = 3;
  }

  sendTotalExoneration(isWithdrawal = false) {
    const exonerateGuaranteRequest: ExonerateGuaranteRequest = {
      exonerateArray: this.selectedOperations,
    };
    this.spinner.show();
    this.exonerationService
      .exonerateGuarante(exonerateGuaranteRequest)
      .subscribe(
        (response: any) => {
          (window as any).gtag(
            "event",
            isWithdrawal
              ? "desistimiento-exitoso"
              : "exoneracion-total-exitosa",
            {
              event_category: isWithdrawal
                ? "desistimiento"
                : "exoneracion-total",
              event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
            }
          );
          this.spinner.hide();
          this.loadOperations();
          this.modal.show("operationsSuccess", {
            title: isWithdrawal
              ? "Desistimiento exitoso"
              : "Exoneración total exitosa",
          });
        },
        (err) => {
          this.spinner.hide();
          this.modal.show("operationsDenied", {
            title: isWithdrawal
              ? "Desistimiento negado"
              : "Exoneración total negada",
          });
        }
      );
  }

  sendNotifyGuarante() {
    const exonerateGuaranteRequest: ExonerateGuaranteRequest = {
      exonerateArray: this.selectedOperations,
    };
    this.spinner.show();
    this.noticeService.notifyGuarante(exonerateGuaranteRequest).subscribe(
      (response: any) => {
        if (this.selectedOperations.length > 1) {
        } else {
          (window as any).gtag("event", "aviso-exitoso", {
            event_category: "aviso-de-no-pago",
            event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
          });
        }
        this.spinner.hide();
        if ("0" in response && response["0"] == "PROCESADO") {
          this.loadOperations();
          this.modal.show("operationsSuccess", {
            title: "Aviso de no pago exitoso",
          });
        } else {
          this.modal.show("nonPaymentError", {
            title: "Aviso de no pago negado",
          });
        }
      },
      (err) => {
        this.spinner.hide();
        this.modal.show("nonPaymentError", {
          title: "Aviso de no pago negado",
        });
      }
    );
  }

  sendPartialExoneration() {
    const amountOperation: OperationItem = this.getAmountItem();
    const exonerateGuaranteRequest: ExonerateGuaranteRequest = {
      exonerateArray: [amountOperation],
    };
    this.spinner.show();
    this.exonerationService
      .exonerateGuarante(exonerateGuaranteRequest)
      .subscribe(
        (response: any) => {
          (window as any).gtag("event", "exoneracion-parcial-exitosa", {
            event_category: "exoneracion-parcial",
            event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
          });
          this.loadOperations();
          this.modal.show("operationsSuccess", {
            title: "Exoneración parcial exitosa",
          });
        },
        (err) => {
          this.spinner.hide();
          this.modal.show("operationsDenied", {
            title: "Exoneración parcial negada",
          });
        }
      );
  }

  onExpirationDateChange(item: OperationItem): void {
    const newExpiration = item.expirationDraft;
    const requestId = ++this.expirationSaveRequestId;
    this.isLoadingDueDate = true;

    this.updateExpirationIfNeeded(item, newExpiration, requestId).subscribe({
      next: () => {
        if (requestId !== this.expirationSaveRequestId) {
          return;
        }
        this.isLoadingDueDate = false;
      },
      error: (err) => {
        if (requestId !== this.expirationSaveRequestId) {
          return;
        }
        console.error('Error updating due date:', err);
        // El calendario vuelve a mostrar la fecha confirmada: la nueva nunca se guardó.
        item.expirationDraft = item.expiration;
        this.isLoadingDueDate = false;
        this.toastr.error('No fue posible actualizar la fecha de vencimiento.', '', {
          timeOut: 2000,
        });
      },
    });
  }

  // ============================================================================
  // Actualización de datos canales digitales (fecha_vencimiento / titulo_valor)
  // ============================================================================

  /**
   * Actualiza la fecha de vencimiento en el backend. Se dispara desde
   * onExpirationDateChange al seleccionar una fecha en el calendario.
   */
  private updateExpirationIfNeeded(
    item: OperationItem,
    newExpiration: string | undefined,
    requestId: number
  ): Observable<any> {
    if (!item || !newExpiration) {
      return of(null);
    }

    const formattedDate = moment(newExpiration).format('YYYY-MM-DD 00:00:00');
    const request = UpdateTitleValueDigitalRequest.forDatePostponement(
      {
        documentType: item.documentType,
        documentNumber: item.documentNumber,
        user: this.auth.identity.userInfo.user,
        authorization: item.authorization,
        titleValue: item.titleValue,
      },
      formattedDate
    );

    return this.operationService.updateTitleValueDigital(request).pipe(
      tap(() => {
        if (requestId === this.expirationSaveRequestId) {
          item.expiration = newExpiration;
        }
      })
    );
  }

  /**
   * Actualiza el título valor en el backend si viene un valor nuevo desde el modal.
   * Si no viene, no hace ninguna llamada.
   */
  private updateTitleValueIfNeeded(
    item: OperationItem,
    newTitleValue?: string
  ): Observable<any> {
    if (!item || !newTitleValue) {
      return of(null);
    }

    const request = UpdateTitleValueDigitalRequest.forTitleValueUpdate(
      {
        documentType: item.documentType,
        documentNumber: item.documentNumber,
        user: this.auth.identity.userInfo.user,
        authorization: item.authorization,
        titleValue: item.titleValue,
      },
      newTitleValue
    );

    return this.operationService.updateTitleValueDigital(request).pipe(
      tap(() => (item.titleValue = newTitleValue))
    );
  }

  /**
   * Ejecuta la acción de negocio correspondiente (exoneración, aviso, desistimiento)
   * según el operationType seleccionado previamente.
   */
  private runOperationByType(): void {
    switch (this.operationType) {
      case 1: {
        (window as any).gtag("event", "confirmacion-obligacion", {
          event_category: "exoneracion-total",
          event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
        });
        this.sendTotalExoneration();
        break;
      }
      case 2: {
        (window as any).gtag("event", "confirmacion-obligacion", {
          event_category: "exoneracion-parcial",
          event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
        });
        this.sendPartialExoneration();
        break;
      }
      case 3: {
        if (this.selectedOperations.length === 1) {
          (window as any).gtag("event", "confirmacion-obligacion", {
            event_category: "aviso-de-no-pago",
            event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
          });
        }
        this.sendNotifyGuarante();
        break;
      }
      case 4: {
        (window as any).gtag("event", "confirmacion-obligacion", {
          event_category: "desistimiento",
          event_label: `${this.auth.identity.userInfo.documentType}:${this.auth.identity.userInfo.document}:1;${this.auth.identity.userInfo.email}`,
        });
        this.sendTotalExoneration(true);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Orquesta la confirmación del modal:
   * 1. Actualiza título valor (si viene uno nuevo desde el modal).
   * 2. Solo si el paso anterior tuvo éxito, ejecuta la acción de negocio.
   */
  sendOperation(result?: { titleValue?: string }) {
    const item = this.selectedOperations.length === 1 ? this.selectedOperations[0] : null;

    if (!item) {
      this.runOperationByType();
      return;
    }

    const newTitleValue = result && result.titleValue;
    
    this.spinner.show();

    this.updateTitleValueIfNeeded(item, newTitleValue).subscribe({
      next: () => {
        this.spinner.hide();
        this.runOperationByType();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('No fue posible actualizar el Título valor / No. Factura. Inténtelo nuevamente', '', {
          timeOut: 3000,
        });
      },
    });
  }

  isamount() {
    const amountOperations: Array<OperationItem> = this.operations.filter(
      (operation: OperationItem) => operation.amount > 0
    );
    return amountOperations.length > 0;
  }

  getAmountItem() {
    const amountOperations: Array<OperationItem> = this.operations.filter(
      (operation: OperationItem) => operation.amount > 0
    );
    const amountOperation: OperationItem =
      amountOperations.length > 0 ? amountOperations[0] : null;
    return amountOperation;
  }

  isAmountError() {
    const amountOperation: OperationItem = this.getAmountItem();
    if (amountOperation) {
      return (
        parseInt(amountOperation.valueToExonerate) < amountOperation.amount
      );
    }
    return false;
  }
}
